import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type {
  CreateGoalRequest,
  GoalQuery,
  GoalResponse,
  UpdateGoalRequest,
} from '@troia-v3/types';
import { goalsApi, type PaginatedGoalResult } from '../services/goalsApi';

const QUERY_KEY = 'goals';

/**
 * Hook to fetch goals with pagination and filters.
 */
export const useGoals = (params?: GoalQuery) => {
  return useQuery<PaginatedGoalResult>({
    queryKey: [QUERY_KEY, params],
    queryFn: () => goalsApi.list(params),
    staleTime: 30_000,
  });
};

/**
 * Hook to fetch a single goal by ID.
 */
export const useGoal = (id: string | undefined) => {
  return useQuery<GoalResponse>({
    queryKey: [QUERY_KEY, id],
    queryFn: () => goalsApi.getById(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
};

/**
 * Hook to create a new goal.
 */
export const useCreateGoal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateGoalRequest) => goalsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Meta criada com sucesso!');
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'Erro ao criar meta';
      toast.error(message);
    },
  });
};

/**
 * Hook to update a goal (partial).
 */
export const useUpdateGoal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateGoalRequest }) =>
      goalsApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, variables.id] });
      toast.success('Meta atualizada com sucesso!');
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'Erro ao atualizar meta';
      toast.error(message);
    },
  });
};

/**
 * Hook to delete a goal.
 */
export const useDeleteGoal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => goalsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Meta excluída com sucesso!');
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'Erro ao excluir meta';
      toast.error(message);
    },
  });
};
